import get from 'lodash/get'
import * as T from '@/core/configs/types'
import { Container } from './container'
import { Note, NoteForm, NoteWrapUp } from './note'
import { getYearsPrependOptions } from '../utils/date'
import {
  HOLD,
  JobAssigned,
  JobCompleted,
  JobProcessing,
  JobUnassigned,
  UNHOLD
} from './constant'
import { AddressBook } from './address'
import { appendBefore } from '../utils'
import { queryCustomers } from '@/api/customer'

export const DirectOrder = 'Direct'
export const UnloadingOrder = 'Unloading'
export const LocalOrder = 'Local'
export const OtherOrder = 'Other'
export const ScanOrder = 'Scan'
export const ScanUploadOrders = 'Scan Upload'
export const OrderTypes = {
  UnloadingOrder,
  LocalOrder,
  DirectOrder,
  OtherOrder,
  ScanOrder,
  ScanUploadOrders
}

export const B2BOrderPlatforms = [
  'Amazon',
  'BestBuy',
  'Target',
  'Other'
]

export const OrderDeleted = 'Deleted'
export const OrderCreated = 'Created'
export const OrderProcessing = 'Processing'
export const OrderCompleted = 'Completed'
export const OrderStatus = {
  OrderDeleted,
  OrderCreated,
  OrderProcessing,
  OrderCompleted
}

export const Accessories = [
  'Delivery appt required',
  'Notify Consignee',
  'Residential Delivery',
  'Liftgate needed'
]

export const DirectDelivery = {
  warehouseName: {
    type: T.Text,
    key: 'warehouseName',
    label: 'Warehouse'
  },
  phone: {
    type: T.Text,
    key: 'phone',
    label: 'Phone'
  },
  email: {
    type: T.Text,
    key: 'email',
    label: 'email'
  },
  attn: {
    type: T.Text,
    key: 'attn',
    label: 'Attn'
  },
  factoryNo: {
    type: T.Text,
    key: 'factoryNo',
    label: 'Factory No.'
  },
  accessories: {
    type: T.Enum,
    key: 'accessories',
    label: 'Accessories',
    options: Accessories
  },
  address: {
    type: T.Text,
    key: 'address',
    label: 'Address'
  },
  comment: {
    type: T.String,
    key: 'comment',
    label: 'Note'
  }
}

export const LocalPallets = {
  address: {
    type: T.Ref,
    ref: AddressBook,
    key: 'address',
    label: 'Address'
  },
  palletCount: {
    type: T.Number,
    key: 'palletCount',
    label: 'Pallet Count'
  },
  refNo: {
    type: T.String,
    key: 'refNo',
    label: 'Customer Ref#'
  },
  fba: {
    type: T.String,
    key: 'fba',
    label: 'FBA'
  },
  notes: {
    type: T.Array,
    itemType: T.Ref,
    ref: Note,
    label: 'Note',
    key: 'notes'
  },
  hold: {
    type: T.Enum,
    options: [UNHOLD, HOLD],
    label: 'Hold',
    key: 'hold'
  }
}

export const Order = {
  id: {
    type: T.String,
    label: 'Order ID',
    key: 'id'
  },
  no: {
    type: T.String,
    label: 'Order No.',
    key: 'no'
  },
  type: {
    type: T.Enum,
    label: 'Order Type',
    key: 'type',
    options: Object.values(OrderTypes),
    col: 'order_type'
  },
  status: {
    type: T.Enum,
    label: 'Order Status',
    key: 'status',
    options: Object.values(OrderStatus),
    col: 'order_status'
  },
  riiOrderNo: {
    type: T.String,
    label: 'RII Order NO.',
    key: 'riiOrderNo',
    col: 'rii_order_no'
  },
  containerId: {
    type: T.String,
    label: 'Container Id',
    key: 'containerId'
  },
  customer: {
    type: T.String,
    label: 'Customer',
    key: 'customer',
    col: 'customer'
  },
  directDeliveryInfo: {
    type: T.Ref,
    ref: DirectDelivery,
    label: 'Delivery Info',
    key: 'directDeliveryInfo'
  },
  localPallets: {
    type: T.Array,
    itemType: T.Ref,
    ref: LocalPallets,
    label: 'Local Pallets',
    key: 'localPallets'
  },
  notes: {
    type: T.Array,
    itemType: T.Ref,
    ref: Note,
    label: 'Note',
    key: 'notes'
  },
  createdTime: {
    type: T.Time,
    label: 'Created on',
    key: 'createdTime',
    col: 'order_created_time'
  },
  updatedTime: {
    type: T.Time,
    label: 'Updated on',
    key: 'updatedTime',
    col: 'order_updated_time'
  },
  deliveryDate: {
    label: 'Delivery Date',
    type: T.Time,
    key: 'deliveryDate',
    col: 'order_delivery_date'
  },
  deliveryCompany: {
    label: 'Delivery Company',
    type: T.String,
    key: 'deliveryCompany'
  }
}

export const OrderForm = Object.fromEntries(
  [
    {
      ...Order.riiOrderNo,
      span: 1,
      type: T.TextInput,
      example: 'OTRII2022110101',
      default: 'OTRII2022110101',
      value: '',
      validators: [T.RequiredValidator]
    },
    {
      ...Order.type,
      span: 1,
      type: T.Select,
      example: 'Scan Delivery',
      default: ScanOrder,
      value: ScanOrder,
      validators: [T.RequiredValidator]
    },
    {
      ...Order.customer,
      span: 1,
      type: T.RemoteSelect,
      options: [],
      default: 'ANKER',
      value: '',
      loading: false,
      method: function (nameLike) {
        const that = this
        that.loading = true
        queryCustomers({ nameLike }).then(res => {
          that.loading = false
          that.options.splice(0, that.options.length)
          this.options.push(
            ...res.map(item => ({
              key: item.name,
              label: item.name,
              value: item.name
            }))
          )
        })
      },
      validators: [T.RequiredValidator]
    },
    ...NoteForm
  ].map(one => [one.key, one])
)

export const LocalOrderForm = {
  ...OrderForm,
  deliveryDate: {
    label: 'Delivery Date',
    span: 1,
    type: T.TimeSelect,
    key: 'deliveryDate',
    example: '',
    default: '',
    value: '',
    validators: [T.RequiredValidator]
  },
  deliveryCompany: {
    label: 'Delivery Company',
    span: 1,
    type: T.TextInput,
    key: 'deliveryCompany',
    example: '',
    default: '',
    value: '',
    validators: [T.RequiredValidator]
  }
}

const OrderStatusTagType = status => {
  return status === OrderDeleted ? 'danger' : 'primary'
}

const OrderInfoPicks = [
  { ...OrderForm.riiOrderNo, type: T.Text, span: 1 },
  { ...OrderForm.customer, type: T.Text, span: 1 },
  { ...OrderForm.type, type: T.Tag, effect: 'dark', span: 1 },
  {
    ...Order.status,
    type: T.Tag,
    effect: 'plain',
    tagType: OrderStatusTagType,
    span: 1
  },
  { ...Order.createdTime, type: T.Time, span: 1 },
  { ...Order.updatedTime, type: T.Time, span: 1 }
]

export const OrderInfoWrapUp = T.generateWrapUp(OrderInfoPicks)

export const OrderInfoModifyForm = Object.fromEntries(
  [
    {
      ...Order.riiOrderNo,
      span: 1,
      type: T.TextInput,
      example: 'RII-1234567',
      default: 'RII-1234567',
      value: 'RII-1234567',
      validators: [T.RequiredValidator],
      convertor: T.cleanString
    },
    {
      ...Order.customer,
      span: 1,
      type: T.RemoteSelect,
      options: [],
      default: 'ANKER',
      value: '',
      loading: false,
      method: function (nameLike) {
        const that = this
        that.loading = true
        queryCustomers({ nameLike }).then(res => {
          that.loading = false
          that.options.splice(0, that.options.length)
          this.options.push(
            ...res.map(item => ({
              key: item.name,
              label: item.name,
              value: item.name
            }))
          )
        })
      },
      validators: [T.RequiredValidator]
    }
  ].map(one => [one.key, one])
)

export const OrderModifyTypeForm = [
  {
    ...Order.type,
    span: 1,
    type: T.Select,
    example: '',
    default: '',
    value: '',
    validators: [T.RequiredValidator]
  }
]

export const DirectDeliveryInfoForm = Object.fromEntries(
  [
    {
      ...DirectDelivery.warehouseName,
      type: T.TextInput,
      span: 1,
      example: 'Walmart',
      default: 'Walmart',
      value: '',
      validators: [T.RequiredValidator]
    },
    {
      ...DirectDelivery.phone,
      type: T.TextInput,
      span: 1,
      example: '+1 668 669 6699',
      default: '+1 668 669 6699',
      value: '',
      validators: []
    },
    {
      ...DirectDelivery.email,
      type: T.TextInput,
      span: 1,
      example: 'riisystem2022@gmail.com',
      default: 'riisystem2022@gmail.com',
      value: '',
      validators: []
    },
    {
      ...DirectDelivery.attn,
      type: T.TextInput,
      span: 1,
      example: 'MC',
      default: 'MC',
      value: '',
      validators: []
    },
    {
      ...DirectDelivery.factoryNo,
      type: T.TextInput,
      span: 1,
      example: 'ONT8',
      default: 'ONT8',
      value: '',
      validators: []
    },
    {
      ...DirectDelivery.accessories,
      type: T.Select,
      span: 1,
      example: Accessories[0],
      default: Accessories[0],
      value: '',
      validators: []
    },
    {
      ...DirectDelivery.address,
      type: T.TextArea,
      span: -1,
      example: '10000 Baybay rods, San Mest, CA, 97177',
      default: '10000 Baybay rods, San Mest, CA, 97177',
      value: '',
      validators: [T.RequiredValidator]
    }
  ].map(one => [one.key, one])
)

const DirectDeliveryInfoPicks = [
  { ...DirectDeliveryInfoForm.warehouseName, type: T.Text, span: 1 },
  { ...DirectDeliveryInfoForm.phone, type: T.Text, span: 1 },
  { ...DirectDeliveryInfoForm.email, type: T.Text, span: 1 },
  { ...DirectDeliveryInfoForm.attn, type: T.Text, span: 1 },
  { ...DirectDeliveryInfoForm.factoryNo, type: T.Text, span: 1 },
  { ...DirectDeliveryInfoForm.accessories, type: T.Tag, span: 1 },
  { ...DirectDeliveryInfoForm.address, type: T.Text, span: -1 }
]

export const DirectDeliveryInfoWrapUp = T.generateWrapUp(
  DirectDeliveryInfoPicks
)

export const OrderTableHeaders = [
  {
    ...Order.riiOrderNo,
    type: T.Link,
    width: 130,
    linkKey: Order.no.key,
    link: 'goToOrderDetail',
    sortable: 'custom',
    defaultSort: true
  },
  {
    ...Container.containerNo,
    width: 120,
    type: T.Text,
    key: 'container.' + Container.containerNo.key,
    sortable: 'custom'
  },
  {
    ...Container.no,
    width: 120,
    type: T.Text,
    key: 'container.' + Container.no.key,
    sortable: 'custom'
  },
  {
    ...Order.status,
    type: T.Tag,
    effect: 'plain',
    tagType: e => {
      switch (e) {
        case OrderStatus.OrderDeleted:
          return 'danger'
        case OrderStatus.OrderProcessing:
          return 'primary'
        case OrderStatus.OrderCreated:
          return 'success'
        default:
          return null
      }
    },
    width: 100,
    sortable: 'custom'
  },
  { ...Order.customer, type: T.Text, sortable: 'custom' },
  {
    ...Container.estimatedDeliveryDate,
    label: 'EST Con Delivery Date',
    type: T.Time,
    width: 150,
    key: 'container.' + Container.estimatedDeliveryDate.key,
    sortable: 'custom'
  },
  {
    ...Container.deliveryDate,
    label: 'Container Delivery Date',
    type: T.Time,
    width: 150,
    key: 'container.' + Container.deliveryDate.key,
    sortable: 'custom'
  }
]

export const LocalOrderTableHeaders = [
  {
    ...Order.riiOrderNo,
    type: T.Link,
    width: 120,
    linkKey: Order.no.key,
    link: 'goToOrderDetail',
    sortable: 'custom',
    defaultSort: true,
    defaultOrder: T.Descending
  },
  {
    ...Order.type,
    type: T.Tag,
    effect: 'plain',
    tagType: 'warn',
    width: 100,
    sortable: 'custom'
  },
  {
    ...Order.status,
    type: T.Tag,
    effect: 'plain',
    tagType: e => {
      switch (e) {
        case OrderStatus.OrderDeleted:
          return 'info'
        case OrderStatus.OrderProcessing:
          return 'primary'
        case OrderStatus.OrderCreated:
          return 'success'
        default:
          return null
      }
    },
    width: 100,
    sortable: 'custom'
  },
  {
    ...Order.localPallets,
    key: 'localPalletsCount',
    type: T.Text
  },
  { ...Order.customer, type: T.Text, sortable: 'custom' },
  {
    ...Order.estimatedDeliveryDate,
    label: 'EST Delivery Date',
    type: T.Time,
    width: 100,
    sortable: 'custom'
  },
  {
    ...Order.createdTime,
    label: 'Created Time',
    type: T.Time,
    width: 100,
    sortable: 'custom'
  }
]

export const ScanOrderTableHeaders = [
  {
    ...Order.riiOrderNo,
    type: T.Link,
    width: 120,
    linkKey: Order.no.key,
    link: 'goToOrderDetail',
    sortable: 'custom',
    defaultSort: true,
    defaultOrder: T.Descending
  },
  {
    type: T.SlotType,
    key: 'vcInfo',
    slotName: 'vcInfo',
    label: 'VC Info',
    width: 120
  },
  {
    ...Order.type,
    type: T.Tag,
    effect: 'plain',
    tagType: 'warn',
    width: 100,
    sortable: 'custom'
  },
  {
    ...Order.customer,
    type: T.Text,
    width: 100,
    sortable: 'custom'
  },
  {
    ...Order.status,
    type: T.Tag,
    effect: 'plain',
    tagType: e => {
      switch (e) {
        case OrderStatus.OrderDeleted:
          return 'info'
        case OrderStatus.OrderProcessing:
          return 'primary'
        case OrderStatus.OrderCreated:
          return 'success'
        default:
          return null
      }
    },
    width: 100,
    sortable: 'custom'
  },
  {
    type: T.SlotType,
    key: 'reports',
    slotName: 'reportCount',
    label: 'Reports',
    width: 60
  },
  {
    ...Order.createdTime,
    label: 'Created Time',
    type: T.Time,
    width: 100,
    sortable: 'custom'
  }
]

export const ShipmentOrderTableHeaders = [
  {
    label: 'Pallet Count',
    key: 'palletCnt',
    type: T.Number,
    width: 120
  },
  ...OrderTableHeaders
]

const OrderSearchTimeTargets = {
  'Order Created Date': {
    key: 'createdTimeRange'
  },
  ETA: {
    key: 'containerETARange'
  },
  'Estimated Delivery Date': {
    key: 'containerEstimatedDeliveryDateRange'
  },
  'Delivery Date': {
    key: 'containerDeliveryDateRange'
  },
  'Empty Date': {
    key: 'containerEmptyDateRange'
  },
  'Local Delivery Date': {
    key: 'deliveryDate'
  }
}

export const OrderSearchForm = [
  {
    label: 'RII Order NO.(after OTRII)',
    key: 'fuzzyRiiOrderNo',
    type: T.TextInput,
    example: 'OTRII2022110101',
    default: '',
    value: '',
    validators: [],
    convertor: (header, value) => {
      if (!value) return ''
      return header.prependValue + value
    },
    prepend: T.PrependText,
    prependValue: 'OTRII'
  },
  {
    label: 'RII Container No.',
    key: 'fuzzyRiiContainerNo',
    type: T.TextInput,
    example: 'C-23-0190',
    inputAccept: 'number',
    default: '',
    value: '',
    validators: [],
    convertor: (header, value) => {
      if (!value) return ''
      return header.prependValue + appendBefore(4, '0', value)
    },
    prepend: T.PrependSelect,
    prependValue: getYearsPrependOptions('C')[0].value,
    prependOptions: getYearsPrependOptions('C')
  },
  {
    label: 'Container No.',
    key: 'fuzzyContainerNo',
    type: T.TextInput,
    example: 'RII-1234567',
    default: '',
    value: '',
    validators: [],
    convertor: T.DefaultConvertor
  },
  {
    ...Order.customer,
    key: 'fuzzyCustomer',
    type: T.TextInput,
    example: 'Anker',
    default: '',
    value: '',
    validators: [],
    convertor: T.DefaultConvertor
  },
  {
    label: 'Order Status',
    key: 'statusList',
    type: T.Select,
    multiple: true,
    example: [],
    default: [OrderCreated, OrderProcessing],
    value: [OrderCreated, OrderProcessing],
    options: [...Order.status.options],
    validators: [T.RequiredNotEmptyListValidator],
    convertor: T.DefaultConvertor
  },
  {
    label: 'SKU',
    key: 'fuzzySku',
    type: T.TextInput,
    example: '',
    default: '',
    value: '',
    validators: [],
    convertor: T.DefaultConvertor
  },
  {
    type: T.Select,
    key: 'timeTarget',
    label: 'Time Target',
    value: '',
    options: [
      'Order Created Date',
      'ETA',
      'Estimated Delivery Date',
      'Container Delivery Date',
      'Empty Date'
    ]
  },
  {
    type: T.TimeRange,
    key: 'timeRange',
    label: 'Time Range',
    value: []
  }
]

export const OrderSearchParamsWrapUp = (type, searchForm) => {
  const params = T.DefaultWarpSearchForm(searchForm)
  const timeRange = params.timeRange
  const timeTarget = params.timeTarget
  if (timeTarget !== undefined && !!timeTarget) {
    params[OrderSearchTimeTargets[timeTarget].key] = timeRange
  }
  params.typeList = [type]
  delete params.timeRange
  delete params.timeTarget
  return params
}

// const LocalPalletsForm = Object.fromEntries(
//   [
//     {
//       ...LocalPallets.address,
//       type: T.AddressSelect,
//       span: 1,
//       default: '',
//       value: '',
//       validators: [T.RequiredValidator]
//     },
//     {
//       ...LocalPallets.palletCount,
//       type: T.NumberInput,
//       span: 1,
//       min: 1,
//       example: '',
//       default: '',
//       value: '',
//       validators: [T.PositiveValidator]
//     },
//     {
//       ...LocalPallets.refNo,
//       span: 1,
//       type: T.TextArea,
//       example: '',
//       default: '',
//       value: '',
//       validators: [T.RequiredValidator]
//     },
//     {
//       ...LocalPallets.fba,
//       type: T.TextInput,
//       span: 1,
//       example: 'FBA2121VVXXX11',
//       default: '',
//       value: '',
//       validators: []
//     },
//     ...NoteForm
//   ].map(one => [one.key, one])
// )

export const LocalPalletForm = Object.fromEntries(
  [
    {
      label: 'Factory No.',
      key: 'address',
      type: T.AddressSelect,
      span: 1,
      default: '',
      value: '',
      validators: []
    },
    {
      label: 'How Many Pallets',
      key: 'palletCount',
      type: T.NumberInput,
      span: 1,
      example: 100,
      default: '',
      value: 1,
      validators: [T.RequiredValidator]
    },
    {
      label: 'Customer Ref# (sep by , )',
      key: 'refNo',
      type: T.TextArea,
      span: 1,
      example: 100,
      default: '',
      value: '',
      validators: [T.RequiredValidator]
    },
    {
      label: 'FBA',
      key: 'fba',
      type: T.TextArea,
      span: 1,
      example: 100,
      default: '',
      value: '',
      validators: []
    },
    {
      label: 'Hold',
      key: 'hold',
      type: T.HoldSelect,
      span: 1,
      value: UNHOLD,
      validators: [T.RequiredValidator]
    },
    {
      label: 'Note',
      key: 'note',
      type: T.TextArea,
      span: 1,
      example: 100,
      default: '',
      value: '',
      validators: []
    }
  ].map(one => [one.key, one])
)

export const ScanPalletsForm = Object.fromEntries(
  [
    {
      label: 'Factory No.',
      key: 'address',
      type: T.AddressSelect,
      span: 1,
      default: '',
      value: '',
      validators: []
    },
    {
      label: 'How Many Pallets',
      key: 'palletCount',
      type: T.NumberInput,
      min: 1,
      span: 1,
      example: 1,
      default: 1,
      value: 1,
      validators: [T.PositiveValidator]
    },
    {
      label: 'Customer Ref#',
      key: 'customerRefNo',
      type: T.TextInput,
      span: 1,
      example: 1,
      default: '',
      value: '',
      validators: [T.RequiredValidator]
    },
    {
      label: 'Hold',
      key: 'hold',
      type: T.HoldSelect,
      span: 1,
      value: UNHOLD,
      default: UNHOLD,
      validators: [T.RequiredValidator]
    },
    {
      label: 'Note',
      key: 'note',
      type: T.TextArea,
      span: 1,
      example: 100,
      default: '',
      value: '',
      validators: []
    }
  ].map(one => [one.key, one])
)

export const LocalPalletGroupHeaders = [
  {
    key: 'address',
    label: 'Destination',
    type: T.AddressTag,
    width: 150
  },
  {
    label: 'Hold',
    key: 'hold',
    type: T.HoldTag
  },
  {
    label: 'Note',
    key: 'note',
    type: T.Text,
    width: 100
  },
  {
    label: 'Pallet CNT',
    key: 'palletCount',
    type: T.Text
  }
]

export const InitLocalPalletsForm = function (inputs) {
  const form = Object.keys(LocalPalletForm).map(key => {
    const one = {
      ...LocalPalletForm[key],
      value: get(inputs, key, LocalPalletForm[key].default)
    }
    return one
  })
  return form
}

export const LocalPalletGroupWrapUp = (values, original) => {
  values.addressId = values.address?.id
  values = NoteWrapUp(values)
  if (original) {
    values.notes = [...original.notes, ...values.notes]
  }
  return values
}

export const MoreLocalPalletsForm = [
  {
    label: 'Address',
    key: 'address',
    type: T.AddressTag,
    span: 1,
    example: '',
    default: '',
    value: '',
    validators: []
  },
  {
    label: 'Previous Count',
    key: 'preCount',
    type: T.Text,
    span: 1,
    example: '',
    default: '',
    value: '',
    validators: []
  },
  {
    label: 'More Count',
    key: 'moreCount',
    type: T.NumberInput,
    min: 1,
    span: 1,
    example: 1,
    default: 1,
    value: 1,
    validators: [T.RequiredValidator]
  },
  {
    label: 'Append to Shipment',
    key: 'appendToShipment',
    type: T.Switch,
    activeText: 'Auto Append',
    inactiveText: 'None',
    span: 1,
    example: false,
    default: 1,
    value: false,
    validators: [T.RequiredValidator]
  }
]

const LocalPalletsInfoPicks = [
  { ...LocalPallets.address, type: T.AddressTag, span: 1 },
  { ...LocalPallets.hold, type: T.HoldTag, span: 1 },
  { ...LocalPallets.palletCount, type: T.Text, span: 1 },
  { ...LocalPallets.refNo, type: T.Text, span: 1 },
  { ...LocalPallets.fba, type: T.Text, span: 1 }
]

export const LocalPalletsInfoWrapUp = T.generateWrapUp(LocalPalletsInfoPicks)

export const LocalPalletsHeaders = [
  { ...LocalPallets.address, width: 100, type: T.AddressTag },
  { ...LocalPallets.hold, width: 100, type: T.HoldTag },
  {
    ...LocalPallets.palletCount,
    width: 100,
    type: T.Number
  },
  { ...LocalPallets.refNo, type: T.Text, width: 200 },
  { ...LocalPallets.fba, type: T.Text, width: 150 }
]

export const LocalOrderSearchForm = [
  {
    label: 'RII Order NO.(after OTRII)',
    key: 'fuzzyRiiOrderNo',
    type: T.TextInput,
    example: 'OTRII2022110101',
    default: '',
    value: '',
    validators: [],
    convertor: (header, value) => {
      return header.prependValue + value
    },
    prepend: T.PrependText,
    prependValue: 'OTRII'
  },
  {
    ...Order.customer,
    key: 'fuzzyCustomer',
    type: T.TextInput,
    example: 'Anker',
    default: '',
    value: '',
    validators: [],
    convertor: T.DefaultConvertor
  },
  {
    label: 'Order Status',
    key: 'statusList',
    type: T.Select,
    multiple: true,
    example: [],
    default: [OrderCreated, OrderProcessing],
    value: [OrderCreated, OrderProcessing],
    options: [...Order.status.options],
    validators: [T.RequiredNotEmptyListValidator],
    convertor: T.DefaultConvertor
  },
  {
    type: T.Select,
    key: 'timeTarget',
    label: 'Time Target',
    value: '',
    options: ['Order Created Date', 'Local Delivery Date']
  },
  {
    type: T.TimeRange,
    key: 'timeRange',
    label: 'Time Range',
    value: []
  }
]

// specific for order unloading
export const UnloadingOrderTableHeaders = [
  {
    ...Order.riiOrderNo,
    type: T.Link,
    width: 120,
    linkKey: Order.no.key,
    link: 'goToOrderDetail',
    sortable: 'custom',
    defaultSort: true,
    defaultOrder: T.Descending
  },
  {
    ...Order.status,
    type: T.Tag,
    effect: 'plain',
    tagType: e => {
      switch (e) {
        case OrderStatus.OrderDeleted:
          return 'info'
        case OrderStatus.OrderProcessing:
          return 'primary'
        case OrderStatus.OrderCreated:
          return 'success'
        default:
          return null
      }
    },
    width: 70,
    sortable: 'custom'
  },
  {
    ...Container.containerNo,
    width: 100,
    type: T.Text,
    key: 'container.' + Container.containerNo.key,
    sortable: 'custom'
  },
  {
    ...Container.no,
    width: 100,
    type: T.Text,
    key: 'container.' + Container.no.key,
    sortable: 'custom'
  },
  {
    label: 'Unloading Job',
    key: 'unloadingJob.no',
    type: T.Link,
    linkKey: 'unloadingJob.no',
    link: 'goToJobDetail',
    col: ''
  },
  {
    label: 'Job Status',
    key: 'unloadingJob.status',
    type: T.Tag,
    effect: 'plain',
    tagType: e => {
      switch (e) {
        case JobUnassigned:
          return 'danger'
        case JobAssigned:
          return 'primary'
        case JobProcessing:
          return 'primary'
        case JobCompleted:
          return 'success'
        default:
          return null
      }
    }
  },
  { ...Order.customer, type: T.Text, sortable: 'custom' },
  {
    ...Container.estimatedDeliveryDate,
    label: 'EST Delivery Date',
    type: T.Time,
    key: 'container.' + Container.estimatedDeliveryDate.key,
    width: 120,
    sortable: 'custom'
  },
  {
    ...Container.deliveryDate,
    type: T.Time,
    key: 'container.' + Container.deliveryDate.key,
    sortable: 'custom'
  }
]

export const UnloadingOrderSearchForm = [
  {
    label: 'RII Order NO.(after OTRII)',
    key: 'fuzzyRiiOrderNo',
    type: T.TextInput,
    inputAccept: 'number',
    example: 'OTRII2022110101',
    default: '',
    value: '',
    validators: [],
    convertor: (header, value) => {
      return header.prependValue + value
    },
    prepend: T.PrependText,
    prependValue: 'OTRII'
  },
  {
    label: 'Container No.',
    key: 'containerNos',
    type: T.TextInput,
    example: 'RII-1234567',
    default: '',
    value: '',
    validators: [],
    convertor: T.DefaultConvertor
  },
  {
    ...Order.customer,
    type: T.TextInput,
    example: 'Anker',
    default: '',
    value: '',
    validators: [],
    convertor: T.DefaultConvertor
  },
  {
    label: 'Order Status',
    key: 'statusList',
    type: T.Select,
    multiple: true,
    example: [],
    default: [OrderCreated, OrderProcessing],
    value: [OrderCreated, OrderProcessing],
    options: [...Order.status.options],
    validators: [T.RequiredNotEmptyListValidator],
    convertor: T.DefaultConvertor
  },
  ...T.PaginationSearchForm
]

export const UnloadingOrderSearchParamsWrapUp = searchForm => {
  const params = T.DefaultWarpSearchForm(searchForm)
  params.typeList = [UnloadingOrder]
  return params
}

export const ScanOrderGroupsTableHeaders = [
  {
    type: T.String,
    label: 'Group Name',
    key: 'id',
    col: 'scan_group_id',
    width: 100
  },
  {
    type: T.String,
    label: 'Customer Ref#',
    key: 'customerRefNo',
    col: 'customer_ref_no',
    width: 100
  },
  {
    ref: AddressBook,
    label: 'Address',
    key: 'address',
    type: T.AddressTag
  },
  {
    type: T.String,
    label: 'Pallet Count',
    key: 'palletCount'
  },
  {
    key: 'totalPakcages',
    label: 'Packages',
    slotName: 'packages',
    type: T.SlotType,
    width: 100
  },
  {
    key: 'orders',
    label: 'Order Nos',
    slotName: 'orders',
    type: T.SlotType,
    width: 300
  }
]

export const OrderVCForm = Object.fromEntries(
  [
    {
      label: 'Platform',
      key: 'type',
      span: 1,
      type: T.Select,
      default: B2BOrderPlatforms[0],
      value: '',
      options: B2BOrderPlatforms,
      validators: [T.RequiredValidator]
    },
    {
      label: 'Carrier',
      key: 'carrierName',
      span: 1,
      type: T.TextInput,
      example: 'Amazon Frieight LTL',
      default: 'Amazon Frieight LTL',
      value: '',
      validators: [T.OnlyCapLetters]
    },
    {
      label: 'BOL',
      key: 'bolNo',
      span: 1,
      type: T.TextInput,
      example: '',
      default: '',
      value: '',
      validators: []
    },
    {
      label: 'Pro No.',
      key: 'proNo',
      span: 1,
      type: T.TextInput,
      example: '',
      default: '',
      value: '',
      validators: [T.NoSpaceOrDash]
    },
    {
      label: 'Purchase No.',
      key: 'poNo',
      span: 1,
      type: T.Select,
      example: '',
      default: '',
      value: '',
      options: [],
      validators: [T.RequiredValidator]
    },
    {
      label: 'ARN No.',
      key: 'arnNo',
      span: 1,
      type: T.TextInput,
      example: '',
      default: '',
      value: '',
      validators: [T.ValidARNNo]
    }
  ].map(one => [one.key, one])
)

export const OrderVCInfoModifyForm = OrderVCForm

const OrderVCInfoPicks = [
  {
    label: 'Platform',
    key: 'type',
    span: 1,
    type: T.Text,
    value: ''
  },
  {
    label: 'Carrier',
    key: 'carrierName',
    span: 1,
    type: T.Text,
    value: ''
  },
  {
    label: 'BOL',
    key: 'bolNo',
    span: 1,
    type: T.Text
  },
  {
    label: 'Pro No.',
    key: 'proNo',
    span: 1,
    type: T.Text
  },
  {
    label: 'Purchase No.',
    key: 'poNo',
    span: 1,
    type: T.Text
  },
  {
    label: 'ARN No.',
    key: 'arnNo',
    span: 1,
    type: T.Text
  }
]

export const OrderVCInfoWrapUp = T.generateWrapUp(OrderVCInfoPicks)

export const ScanOrderPreviewTableHeaders = [
  {
    type: T.String,
    label: 'RII Order No',
    key: 'riiOrderNo',
    width: 120
  },
  {
    type: T.String,
    label: '魔方订单号',
    key: 'cubeOrderNo',
    width: 120
  },
  {
    type: T.String,
    label: 'Customer',
    key: 'customer',
    width: 120
  },
  {
    type: T.String,
    label: 'Platform',
    key: 'vcInfo.type',
    width: 60
  },
  {
    type: T.String,
    label: 'Carrier',
    key: 'vcInfo.carrierName',
    width: 120
  },
  {
    type: T.String,
    label: 'BOL',
    key: 'vcInfo.bolNo',
    width: 120
  },
  {
    type: T.String,
    label: 'Pro No',
    key: 'vcInfo.proNo',
    width: 120
  },
  {
    type: T.String,
    label: 'Purchase No',
    key: 'vcInfo.poNo',
    width: 120
  },
  {
    type: T.String,
    label: 'ARN No',
    key: 'vcInfo.arnNo',
    width: 120
  }
]

export const ScanOrderExcelHeaders = [
  {
    label: '魔方订单号',
    key: 'cubeOrderNo',
    header: '魔方订单号',
    width: 90,
    type: T.String,
    validators: [],
    convertor: T.cleanString
  },
  {
    label: 'Customer',
    key: 'customer',
    header: 'Customer',
    width: 90,
    type: T.String,
    validators: [],
    convertor: T.cleanString
  },
  {
    label: 'Comment',
    key: 'comment',
    header: 'Comment',
    width: 90,
    type: T.String,
    validators: [],
    convertor: T.cleanString
  },
  {
    label: 'Platform',
    key: 'platform',
    header: 'Platform',
    width: 90,
    type: T.String,
    validators: [(val) => {
      const flag = B2BOrderPlatforms.includes(val)
      if (flag) return [true, '']
      return [false, 'invalid platform type ' + val]
    }],
    convertor: T.cleanString
  },
  {
    label: 'Carrier',
    key: 'carrierName',
    header: 'Carrier',
    width: 90,
    type: T.String,
    validators: [T.OnlyCapLetters],
    convertor: T.cleanString
  },
  {
    label: 'BOL',
    key: 'bolNo',
    header: 'BOL',
    width: 90,
    type: T.String,
    validators: [],
    convertor: T.cleanString
  },
  {
    label: 'Pro No.',
    key: 'proNo',
    header: 'Pro No',
    width: 90,
    type: T.String,
    validators: [T.NoSpaceOrDash],
    convertor: T.cleanString
  },
  {
    label: 'Purchase No.',
    key: 'poNo',
    header: 'Purchase No',
    width: 90,
    type: T.String,
    validators: [T.ValidPoNumber],
    convertor: T.cleanString
  },
  {
    label: 'ARN No.',
    key: 'arnNo',
    header: 'ARN No',
    width: 90,
    type: T.String,
    validators: [T.ValidARNNo],
    convertor: T.cleanString
  },
  {
    label: 'Factory No',
    key: 'factoryNo',
    header: 'Factory No',
    width: 90,
    type: T.String,
    validators: [],
    convertor: T.cleanString
  },
  {
    label: 'Pallet Count',
    key: 'palletCount',
    header: 'Pallet Count',
    width: 90,
    type: T.Number,
    validators: [T.PositiveValidator],
    convertor: T.toInteger
  },
  {
    label: 'Customer Ref No',
    key: 'customerRefNo',
    header: 'Customer Ref No',
    width: 90,
    type: T.String,
    validators: [],
    convertor: T.cleanString
  },
  {
    label: 'Note',
    key: 'note',
    header: 'Note',
    width: 90,
    type: T.String,
    validators: [],
    convertor: T.cleanString
  },
  {
    label: 'SKU',
    key: 'productNo',
    header: 'SKU',
    width: 90,
    type: T.String,
    validators: [],
    convertor: T.cleanString
  },
  {
    label: '商品名称',
    key: 'productName',
    header: '商品名称',
    width: 90,
    type: T.String,
    validators: [],
    convertor: T.cleanString
  },
  {
    label: '总件数',
    key: 'quantity',
    header: '总件数',
    width: 90,
    type: T.Number,
    validators: [T.PositiveValidator],
    convertor: T.toInteger
  },
  {
    label: '总箱数',
    key: 'cartons',
    header: '总箱数',
    width: 90,
    type: T.Number,
    validators: [T.PositiveValidator],
    convertor: T.toInteger
  },
  {
    label: '单箱件数',
    key: 'perPackageQty',
    header: '单箱件数',
    width: 90,
    type: T.Number,
    validators: [T.PositiveValidator],
    convertor: T.toInteger
  }
]

export function processUploadScanOrders(rows) {
  const orders = {}
  let i = 0
  while (i < rows.length) {
    const currentRow = rows[i]
    if (currentRow.cubeOrderNo in orders) {
      const groupLength = orders[currentRow.cubeOrderNo].goodGroups.length
      const lastGroup = orders[currentRow.cubeOrderNo].goodGroups?.[groupLength - 1] ?? null
      const flag1 = lastGroup && currentRow.factoryNo === lastGroup.factoryNo
      const flag2 = lastGroup && currentRow.customerRefNo === lastGroup.customerRefNo
      const flag3 = lastGroup && currentRow.note === lastGroup.note
      if (flag1 && flag2 && flag3) {
        // belongs to the same group
        lastGroup.products.push({
          factoryNo: currentRow.factoryNo,
          customerRefNo: currentRow.customerRefNo,
          productNo: currentRow.productNo,
          productName: currentRow.productName,
          quantity: currentRow.quantity,
          cartons: currentRow.cartons,
          customerOrderNo: currentRow.cubeOrderNo,
          perPackageQty: currentRow.perPackageQty,
          riiCustomer: currentRow.customer,
          platform: currentRow.platform
        })
      } else {
        orders[currentRow.cubeOrderNo].goodGroups.push({
          note: currentRow.note,
          factoryNo: currentRow.factoryNo,
          customerRefNo: currentRow.customerRefNo,
          palletCount: currentRow.palletCount,
          products: [
            {
              factoryNo: currentRow.factoryNo,
              customerRefNo: currentRow.customerRefNo,
              productNo: currentRow.productNo,
              productName: currentRow.productName,
              quantity: currentRow.quantity,
              cartons: currentRow.cartons,
              customerOrderNo: currentRow.cubeOrderNo,
              perPackageQty: currentRow.perPackageQty,
              riiCustomer: currentRow.customer,
              platform: currentRow.platform
            }
          ]
        })
      }
    } else {
      orders[currentRow.cubeOrderNo] = {
        riiOrderNo: `OTRII${currentRow.cubeOrderNo}`,
        cubeOrderNo: currentRow.cubeOrderNo,
        customer: currentRow.customer,
        note: currentRow.comment,
        platform: currentRow.platform,
        vcInfo: {
          type: currentRow.platform,
          carrierName: currentRow.carrierName,
          bolNo: currentRow.bolNo,
          proNo: currentRow.proNo,
          arnNo: currentRow.arnNo,
          poNo: currentRow.poNo
        },
        goodGroups: [
          {
            note: currentRow.note,
            factoryNo: currentRow.factoryNo,
            customerRefNo: currentRow.customerRefNo,
            palletCount: currentRow.palletCount,
            products: [
              {
                factoryNo: currentRow.factoryNo,
                customerRefNo: currentRow.customerRefNo,
                productNo: currentRow.productNo,
                productName: currentRow.productName,
                quantity: currentRow.quantity,
                cartons: currentRow.cartons,
                customerOrderNo: currentRow.cubeOrderNo,
                perPackageQty: currentRow.perPackageQty,
                riiCustomer: currentRow.customer,
                platform: currentRow.platform
              }
            ]
          }
        ]
      }
    }
    i += 1
  }
  return Object.values(orders)
}

export const ScanOrderPreviewGoodGroupTableHeaders = [
  {
    type: T.String,
    label: 'Factory No',
    key: 'factoryNo',
    width: 120
  },
  {
    ref: AddressBook,
    label: 'Address',
    key: 'address',
    type: T.AddressTag
  },
  {
    type: T.String,
    label: 'Customer Ref No',
    key: 'customerRefNo',
    width: 140
  },
  {
    label: 'SKU',
    key: 'productNo',
    type: T.String,
    width: 120
  },
  {
    key: 'quantity',
    label: '总件数',
    type: T.Number,
    width: 120
  },
  {
    key: 'perPackageQty',
    label: '单箱件数',
    type: T.Number,
    width: 120
  },
  {
    key: 'cartons',
    label: '总箱数',
    type: T.Number,
    width: 120
  }
]

export function validateScanOrderStock(
  customer,
  vcInfo,
  stock
) {
  const platform = vcInfo.type
  for (const one of stock) {
    const invalidProducts = one.products
      .filter(one => one.platform != null)
      .filter(one => one.platform !== platform)
    if (invalidProducts.length > 0) {
      return [false, 'order ' + invalidProducts[0].customerOrderNo + ' is not for ' + platform]
    }
    const invalidCustomers = one.products
      .filter(one => one.riiCustomer !== customer)
    if (invalidCustomers.length > 0) {
      return [false, 'order ' + invalidCustomers[0].customerOrderNo + ' is not for ' + customer]
    }
  }
  return [true, undefined]
}
